/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Col,
  Form,
  FormInstance,
  Modal,
  Row,
  Select,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import { NamePath } from 'antd/lib/form/interface';
import classNames from 'classnames';
import { Header } from 'components/header';
import { AngleIcon, TrashIcon } from 'components/icons';
import { LevelsCollapse } from 'components/levels-collapse';
import { SelectOption } from 'components/select';
import { useEmployee } from 'hooks';
import { usePlatformDictionary } from 'hooks/use-platform-dictionary';
import { useSavePlatforms } from 'hooks/use-save-platforms';
import { useSkillLevelDictionary } from 'hooks/use-skill-levels-dictionary';
import { EpTechnologySkill } from 'types/graphql-types';
import styles from './styles.less';

export type PlatformsFormProps = {
  editMode?: boolean;
  setEditMode?: (value: boolean) => void;
  formName?: string;
  form?: FormInstance;
};

type FormValues = Partial<Record<string, string[]>>;

const renderPlatField = (
  level: string,
  options: SelectOption[],
  onDelete: () => void,
  onChange: (values: string[], name: NamePath) => void,
): React.ReactNode => (
  <Row
    key={level}
    gutter={[0, 6]}
    className={classNames(styles['row'], styles['data-row'])}
  >
    <Col span={16}>
      <Form.Item
        name={level}
        className={styles['form-item']}
        rules={[
          {
            validator: (_rule: unknown, value: string[], callback) => {
              if (value.find((str) => str.length > 30)) {
                callback('Platform should not exceed 30 characters');
              }

              callback();
            },
          },
        ]}
      >
        <Select
          options={options}
          mode={'tags'}
          onChange={(values) => onChange(values as string[], level)}
          placeholder={`Choose your ${level} level platforms`}
          suffixIcon={<AngleIcon />}
        />
      </Form.Item>
    </Col>
    <Col span={8}>
      <div className={styles['level']}>
        {level}
        <TrashIcon onClick={onDelete} />
      </div>
    </Col>
  </Row>
);

const renderPlatTags = (
  plats: EpTechnologySkill[],
  level: string,
): React.ReactNode => (
  <Row key={level} className={classNames(styles['row'], styles['data-row'])}>
    <Col span={16}>
      <Row gutter={[0, 6]}>
        {plats.map((plat) =>
          plat.desc === 'EP' ? (
            <Col key={plat.name}>
              <Tag>{plat.name}</Tag>
            </Col>
          ) : (
            <Col key={plat.name}>
              <Tooltip
                placement="bottom"
                title={
                  'You added this platform yourself. So far, only you can see this skill.'
                }
              >
                <Tag className={styles['custom-tech']}>{plat.name}</Tag>
              </Tooltip>
            </Col>
          ),
        )}
      </Row>
    </Col>
    <Col span={8}>
      <div className={styles['level']}>{level}</div>
    </Col>
  </Row>
);

export const PlatformsForm: FC<PlatformsFormProps> = (props) => {
  const { editMode = false, formName, form, setEditMode } = props;
  const { t } = useTranslation();
  const { employee, error } = useEmployee();

  const { data: levelData } = useSkillLevelDictionary();
  const levels = levelData?.skillLevelList?.slice().reverse() || [];

  const { data: platData } = usePlatformDictionary();
  const platList = platData?.platformList || [];

  const [availablePlats, setAvailablePlats] = useState(
    platList.filter(
      (plat) => !employee?.platforms?.find((x) => x.name === plat),
    ),
  );

  const recalculateAvailablePlats = (): void => {
    const values = form?.getFieldsValue() as FormValues;
    const takenPlats = Object.values(values).flat() as string[];

    setAvailablePlats(platList.filter((plat) => !takenPlats.includes(plat)));
  };

  const platOptions = availablePlats.map((plat) => ({
    label: plat,
    value: plat,
  }));

  const savePlatforms = useSavePlatforms();

  if (!employee || error) {
    return <Alert message={error?.message} showIcon type="error" />;
  }

  const plats = employee.platforms || [];

  const platsInitialValues: Record<string, string[]> = levels.reduce(
    (acc, level) => ({
      ...acc,
      [level]: plats
        .filter((plat) => plat.level === level)
        .map((plat) => plat.name),
    }),
    {} as Record<string, string[]>,
  );

  const onSave = (values: FormValues): void => {
    const platsToSave: EpTechnologySkill[] = [];

    Object.entries(values).forEach(([level, value]) => {
      platsToSave.push(
        ...(value || []).map((plat) => ({
          desc: platList.find((x) => x === plat) ? 'EP' : employee.login,
          level,
          name: plat,
        })),
      );
    });
    void savePlatforms({ platforms: platsToSave })
      .then(() => {
        notification.success({
          message: t('Platforms have been saved!'),
        });
        setEditMode && setEditMode(false);
      })
      .catch(() =>
        notification.error({
          message: t('Something has gone wrong! Try again!'),
        }),
      );
  };

  const onDelete = (level: string): void => {
    Modal.confirm({
      content: t(
        `Are you sure you want to delete your ${level} level platforms?`,
      ),
      okText: t('Yes, delete'),
      onOk: () => {
        form?.setFieldValue(level, []);
        recalculateAvailablePlats();
      },
      title: t('Confirm action'),
    });
  };

  const onMultiselectChange = (
    newFieldValues: string[],
    name: NamePath,
  ): void => {
    const fieldValues = form?.getFieldsValue() as FormValues;
    const values = Object.values(fieldValues || []).flat() as string[];

    const valuesToDelete: string[] = [];

    newFieldValues
      .slice()
      .reverse()
      .forEach((plat) => {
        if (!valuesToDelete.includes(plat)) {
          const duplicate = values.find(
            (x) =>
              x.toUpperCase() === plat.toUpperCase() &&
              (x !== plat || values.filter((y) => y === x).length > 1),
          );

          if (
            duplicate &&
            !valuesToDelete.find(
              (x) => x.toUpperCase() === duplicate.toUpperCase(),
            )
          ) {
            valuesToDelete.push(plat);
          }
        }
      });
    const newValues = newFieldValues.filter(
      (plat) => !valuesToDelete.includes(plat),
    );

    form?.setFieldValue(name, newValues);
    recalculateAvailablePlats();
  };

  if (plats.length === 0 && !editMode) {
    return t('Add your first platform skill');
  }

  return (
    <Row>
      <Col xs={{ span: 24 }} lg={{ span: 16 }}>
        <Form
          name={formName}
          form={form}
          initialValues={platsInitialValues}
          onFinish={onSave}
          preserve={false}
          onFieldsChange={recalculateAvailablePlats}
        >
          <Row className={classNames(styles['row'], styles['header-row'])}>
            <Col span={16}>
              <Header level={5} className={styles['header']}>
                {t('Platform name')}
              </Header>
            </Col>
            <Col span={8}>
              <Header level={5} className={styles['header']}>
                {t('Experience')}
              </Header>
            </Col>
          </Row>
          {editMode
            ? levels.map((level) =>
                renderPlatField(
                  level,
                  platOptions,
                  () => onDelete(level),
                  onMultiselectChange,
                ),
              )
            : levels.map((level) => {
                const filteredPlats = plats.filter(
                  (plat) => plat.level === level,
                );

                return filteredPlats.length > 0
                  ? renderPlatTags(filteredPlats, level)
                  : null;
              })}
        </Form>
      </Col>
      <Col xs={{ span: 0 }} lg={{ span: 8 }}>
        <LevelsCollapse />
      </Col>
    </Row>
  );
};
